import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, PenLine, Trash2 } from 'lucide-react';
import Layout from '../../components/Layout';
import PermissionTree from '../../components/PermissionTree';
import { roleService } from '../../services/roleService';
import { RoleDetail } from '../../types';
import { useModule } from '../../context/ModuleContext';
import { filterPermissionsByModule } from '../../utils/permissionModuleVisibility';
import '../../components/PermissionTree.css';
import '../Products/ProductCategoryDetailPage.css';
import '../Products/ProductCategoryCreatePage.css';

const RoleDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { moduleCode } = useModule();
  const [role, setRole] = useState<RoleDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage] = useState<string | null>(
    (location.state as { message?: string } | null)?.message ?? null
  );

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    roleService.getRoleById(id)
      .then((res) => {
        setRole(res.data?.data ?? null);
      })
      .catch(() => setError(t('role.failedToLoad')))
      .finally(() => setLoading(false));
  }, [id, t]);

  if (loading) {
    return (
      <Layout>
        <div className="detail-loading-container">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </Layout>
    );
  }

  if (!role) {
    return (
      <Layout>
        <div className="create-container">
          <div className="alert alert-danger">{error || t('role.roleNotFound')}</div>
          <button className="ims-back-btn" onClick={() => navigate('/roles')}>
            <ArrowLeft size={16} strokeWidth={2} />
          </button>
        </div>
      </Layout>
    );
  }

  const visiblePermissions = filterPermissionsByModule(role.permissions, moduleCode);
  const selectedIds = visiblePermissions.map((p) => p.id);

  return (
    <Layout>
      <div className="create-container">
        <div className="ims-top-bar">
          <div className="ims-top-bar-left">
            <button type="button" className="ims-back-btn" onClick={() => navigate('/roles')}>
              <ArrowLeft size={16} strokeWidth={2} />
            </button>
            <div className="ims-page-title">{role.name}</div>
          </div>
          <div className="ims-top-bar-right">
            <button
              type="button"
              className="ims-btn ims-btn-secondary"
              onClick={() => navigate(`/roles/${role.id}/edit`)}
            >
              <PenLine size={15} strokeWidth={2} />
              {t('common.edit')} {t('role.title').slice(0, -1)}
            </button>
            {!role.isSystem && (
              <button
                type="button"
                className="ims-btn ims-btn-danger"
                onClick={() =>
                  navigate(`/roles/${role.id}/delete`, { state: { roleName: role.name } })
                }
              >
                <Trash2 size={15} strokeWidth={2} />
                {t('common.delete')} {t('role.title').slice(0, -1)}
              </button>
            )}
          </div>
        </div>

        {successMessage && (
          <div className="alert alert-success alert-dismissible fade show">
            <strong>{t('common.success')}:</strong> {successMessage}
          </div>
        )}

        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            {error}
            <button type="button" className="btn-close" onClick={() => setError(null)} />
          </div>
        )}

        <div className="detail-content">
          <div className="detail-main-section">

            {/* Role info */}
            <div className="detail-card">
              <div className="detail-card-header">
                <h2 className="detail-card-title">{t('role.roleDetails')}</h2>
              </div>
              <div className="detail-card-body">
                <div className="detail-grid">
                  <div className="detail-field">
                    <span className="detail-label">{t('role.roleName')}</span>
                    <span className="detail-value">{role.name}</span>
                  </div>
                  <div className="detail-field">
                    <span className="detail-label">{t('role.type')}</span>
                    <span className="detail-value">
                      <span className={`badge ${role.isSystem ? 'badge-draft' : 'badge-active'}`}>
                        {role.isSystem ? t('role.systemRole') : t('role.customRole')}
                      </span>
                    </span>
                  </div>
                  <div className="detail-field" style={{ gridColumn: '1 / -1' }}>
                    <span className="detail-label">{t('role.description')}</span>
                    <span className="detail-value">{role.description || '—'}</span>
                  </div>
                  {role.createdAt && (
                    <div className="detail-field">
                      <span className="detail-label">{t('role.createdDate')}</span>
                      <span className="detail-value">
                        {new Date(role.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Permissions TreeView */}
            <div className="detail-card">
              <div className="detail-card-header">
                <h2 className="detail-card-title">{t('role.assignedPermissions')}</h2>
              </div>
              <div className="detail-card-body">
                {visiblePermissions.length === 0 ? (
                  <p style={{ color: '#6b7280', fontSize: '14px', margin: 0 }}>
                    {t('role.noPermissionsAssigned')}
                  </p>
                ) : (
                  <PermissionTree
                    allPermissions={visiblePermissions}
                    selectedIds={selectedIds}
                    readOnly
                  />
                )}
              </div>
            </div>

          </div>

          {/* Sidebar — summary only, actions moved to top bar */}
          <div className="detail-sidebar">
            <div className="detail-card">
              <div className="detail-card-header">
                <h3 className="detail-card-title">{t('role.summary')}</h3>
              </div>
              <div className="detail-card-body">
                <div style={{ fontSize: '13px', lineHeight: '2' }}>
                  <div>
                    <span style={{ fontWeight: 600, color: '#374151' }}>{t('role.permissionsCount')}:</span>{' '}
                    <span style={{ color: '#6b7280' }}>{visiblePermissions.length}</span>
                  </div>
                  <div>
                    <span style={{ fontWeight: 600, color: '#374151' }}>{t('role.usersCount')}:</span>{' '}
                    <span style={{ color: '#6b7280' }}>{role.userCount}</span>
                  </div>
                  <div>
                    <span style={{ fontWeight: 600, color: '#374151' }}>{t('role.module')}s:</span>{' '}
                    <span style={{ color: '#6b7280' }}>
                      {[...new Set(visiblePermissions.map((p) => p.module))].length}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default RoleDetailPage;
